import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationService } from '../../services/auth/authentication.service';
import { ApiService } from '../../services/api/api.service';
import { FormatService } from '../../services/format/format.service';
import * as moment from 'moment';

// Read-only monitoring of Muraja'ah (revision of memorized material: the
// user's Juz / Surat / Halaman list + review sessions) — same page anatomy as
// the Tahfidz page: KPI strip + segments (Pengguna | Sesi).
@Component({
  selector: 'app-murojaah',
  templateUrl: './murojaah.component.html',
  styleUrls: ['./murojaah.component.css']
})
export class MurojaahComponent implements OnInit {
  mView : string = 'users'; // users | sessions
  mkpi : any = null;
  directoryFile : string = '';
  isPageSize : number = 10;

  // ===== users =====
  mUsers : any = [];
  mUsersTotal : number = 0;
  mKeyword : string = '';
  mSort : string = 'activity';
  mUsersPage : number = 1;
  mUsersPages : any = [];
  mUsersLoading : boolean = false;

  // ===== sessions =====
  mSessions : any = [];
  mSessionsTotal : number = 0;
  mDays : string = '30';
  mSessionsPage : number = 1;
  mSessionsPages : any = [];
  mSessionsLoading : boolean = false;

  constructor(
    private router: Router,
    private authService: AuthenticationService,
    private apiService: ApiService,
    private formatService: FormatService,
  ) { }

  ngOnInit(): void {
    moment.locale('id');
    this.isPageSize = this.apiService.LIMIT || 10;
    this.loadSummary();
    this.getMurojaahUsers();
  }

  loadSummary(){
    this.apiService.methodGetSilent(`murojaah/summary`, this.authService.getToken()).subscribe((response) => {
      if(response && response.status == 200){ this.mkpi = response.data; }
    });
  }
  murojaahLancarPct(){
    const k = this.mkpi;
    const total = k ? k.lancar_30d + k.tersangkut_30d + k.lupa_30d : 0;
    return total ? Math.round(k.lancar_30d / total * 100) : 0;
  }

  setView(view : string){
    if(this.mView === view){ return; }
    this.mView = view;
    if(view === 'users' && !this.mUsers.length && !this.mUsersLoading){ this.getMurojaahUsers(); }
    if(view === 'sessions' && !this.mSessions.length && !this.mSessionsLoading){ this.getMurojaahSessions(); }
  }

  // the detail page is per USER; pass the identity along so its header
  // renders instantly (the muraja'ah detail API carries no user fields)
  openUser(id : any, name : string, email : string, photoUrl : string){
    this.router.navigate(['/murojaah', id], {state: {user: {name, email, photoUrl}}});
  }

  // ===== filters =====
  private mTimer : any;
  private mLastQuery : string = '';
  onMurojaahKeywordInput(value : string){
    this.mKeyword = value;
    clearTimeout(this.mTimer);
    // debounce: wait 450ms after the last keystroke before hitting the API
    this.mTimer = setTimeout(() => this.applyMurojaahFilter(), 450);
  }
  applyMurojaahFilter(){
    clearTimeout(this.mTimer);
    const query = `${this.mView}|${this.mKeyword}|${this.mSort}|${this.mDays}`;
    if(query === this.mLastQuery){ return; } // query unchanged, skip the request
    this.mLastQuery = query;
    if(this.mView === 'sessions'){ this.mSessionsPage = 1; this.getMurojaahSessions(); }
    else { this.mUsersPage = 1; this.getMurojaahUsers(); }
  }

  // ===== users =====
  getMurojaahUsers(){
    const searchData = {"keyword": this.mKeyword, "sort": this.mSort};
    const offset = (this.mUsersPage - 1) * this.isPageSize;
    this.mUsersLoading = true;
    this.apiService.methodGetSilent(`murojaah/users/${offset}/${encodeURIComponent(JSON.stringify(searchData))}`, this.authService.getToken()).subscribe((response) => {
      this.mUsersLoading = false;
      if(response && response.status == 200){
        this.directoryFile = response.directoryFile || this.directoryFile;
        this.mUsers = (response.data || []).map((u : any) => {
          u.photoUrl = this.photoUrl(u.photo, 'user/avatar/');
          u.nameVw = u.name || 'Akun tidak ditemukan';
          u.lastItemVw = !u.items_total ? '' : this.reviewedAgo(u.last_item_ago);
          u.lastSessionVw = u.last_session_at ? moment(u.last_session_at).fromNow() : 'Belum pernah';
          u.lastSessionFull = u.last_session_at ? moment(u.last_session_at).format('D MMMM YYYY HH:mm') : '';
          return u;
        });
        this.mUsersTotal = response.total || 0;
        this.mUsersPages = this.pagesFor(this.mUsersPage, this.pagesOf(this.mUsersTotal));
      }
    }, () => { this.mUsersLoading = false; });
  }
  // "Hari ini" / "terakhir diulang N hari lalu" / "Belum diulang"
  reviewedAgo(days : any){
    if(days == null){ return 'Belum diulang'; }
    if(days <= 0){ return 'Hari ini'; }
    return `terakhir diulang ${this.formatNumber(days)} hari lalu`;
  }
  goMurojaahUsersPage(p : any){
    if(p < 1 || p > this.pagesOf(this.mUsersTotal) || p == this.mUsersPage){ return; }
    this.mUsersPage = p; this.getMurojaahUsers();
  }

  // ===== sessions =====
  getMurojaahSessions(){
    const searchData = {"keyword": this.mKeyword, "days": this.mDays};
    const offset = (this.mSessionsPage - 1) * this.isPageSize;
    this.mSessionsLoading = true;
    this.apiService.methodGetSilent(`murojaah/sessions/${offset}/${encodeURIComponent(JSON.stringify(searchData))}`, this.authService.getToken()).subscribe((response) => {
      this.mSessionsLoading = false;
      if(response && response.status == 200){
        this.directoryFile = response.directoryFile || this.directoryFile;
        this.mSessions = (response.data || []).map((s : any) => this.decorateSession(s));
        this.mSessionsTotal = response.total || 0;
        this.mSessionsPages = this.pagesFor(this.mSessionsPage, this.pagesOf(this.mSessionsTotal));
      }
    }, () => { this.mSessionsLoading = false; });
  }
  goMurojaahSessionsPage(p : any){
    if(p < 1 || p > this.pagesOf(this.mSessionsTotal) || p == this.mSessionsPage){ return; }
    this.mSessionsPage = p; this.getMurojaahSessions();
  }
  // shared with the detail page's session feed — keep in sync
  decorateSession(s : any){
    s.userName = s.user_name || 'Akun tidak ditemukan';
    s.userPhotoUrl = this.photoUrl(s.user_photo, 'user/avatar/');
    s.juzVw = s.juz && s.juz.length ? 'Juz ' + s.juz.join(', ') : '';
    s.timeVw = s.finished_at ? moment(s.finished_at).fromNow() : '';
    s.timeFull = s.finished_at ? moment(s.finished_at).format('D MMMM YYYY HH:mm') : '';
    s.dayVw = s.finished_at ? moment(s.finished_at).format('D MMM YYYY, HH:mm') : this.dayLabel(s.day);
    s.durationVw = s.duration_min == null ? '' : s.duration_min < 1 ? '< 1 mnt' : `${this.formatNumber(s.duration_min)} mnt`;
    s.isClean = !s.tersangkut_count && !s.lupa_count;
    return s;
  }

  // ===== helpers =====
  // epoch-day integer (local days since 1970, see the app's hafalan_day.dart) → "12 Sep 2026"
  dayLabel(day : any){
    if(day == null){ return ''; }
    return moment.utc(Number(day) * 86400000).format('D MMM YYYY');
  }
  photoUrl(photo : any, folder : string){
    if(!photo){ return ''; }
    return String(photo).includes('http') ? photo : `${this.directoryFile}${folder}${photo}`;
  }
  pagesFor(current : number, total : number){
    const pages: any[] = [];
    for(let p = 1; p <= total; p++){
      if(p == 1 || p == total || Math.abs(p - current) <= 1){
        pages.push(p);
      }else if(pages[pages.length - 1] != '...'){
        pages.push('...');
      }
    }
    return pages;
  }
  rangeFrom(page : number, total : number){ return total ? (page - 1) * this.isPageSize + 1 : 0; }
  rangeTo(page : number, len : number){ return (page - 1) * this.isPageSize + len; }
  pagesOf(total : number){ return Math.max(1, Math.ceil(total / this.isPageSize)); }
  formatNumber(value : any){ return this.formatService.dotFormat(value || 0); }

  // wrap keyword matches in <mark> for the list; input is HTML-escaped first
  highlight(text : any, keyword : string = this.mKeyword){
    const esc = String(text == null ? '' : text)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const kw = (keyword || '').trim();
    if(!kw){ return esc; }
    const escapedKeyword = kw
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // neutralize regex metacharacters
    return esc.replace(new RegExp(`(${escapedKeyword})`, 'gi'), '<mark class="vl-hl">$1</mark>');
  }
}
